<template>
  <div class="food-item-edit">
    <div class="main">
      <div class="title_bar">
        <div class="left">
          <div class="title">
            {{ id === '0' ? $t("General.Create") : $t("General.Edit") }}{{ $t("OrderFood.Spec") }}
          </div>
        </div>
        <div class="right food-item-edit__right">
          <div v-if="id !== '0'">
            <el-button
              class="mr-24"
              plain
              type="danger"
              size="mini"
              @click="handleClickDelete"
            >
              {{ $t("General.Delete") }}
            </el-button>
          </div>
          <div class="food-item-edit__required">
            <div>*</div>
            <div>{{ $t('OrderFoodItemManagement.AsRequired') }}</div>
          </div>
        </div>
      </div>
      <div class="food-item-edit__form-wrapper">
        <el-form
          ref="editFormRef"
          class="food-item-edit__form-el"
          :model="editForm"
          status-icon
          @submit.native.prevent
        >
          <el-form-item
            :label="$t('OrderFood.Spec')"
            prop="name"
            :rules="{ required: true, message: $t('OrderFoodSpecEdit.PleaseEnterFoodSpec'), trigger: ['change', 'blur'] }"
          >
            <el-input
              v-model="editForm.name"
              maxlength="10"
              :placeholder="$t('OrderFoodSpecEdit.SpecNamePlaceholder')"
            />
          </el-form-item>
          <div class="food-item-edit__table-wrapper">
            <ItemsListSortable ref="sortableRef" class="food-item-edit__sortable" @drag-end="handleDragEnd">
              <el-table ref="tableRef" :data="editForm.children" row-key="timestamp">
                <el-table-column width="60" align="center">
                  <div class="food-item-edit__drag-btn">
                    <el-button type="primary" class="drag-handle" icon="el-icon-d-caret" circle size="mini"></el-button>
                  </div>
                </el-table-column>
                <el-table-column :label="$t('OrderFood.SpecOptions')" prop="name">
                  <template slot-scope="scope">
                    <el-form-item
                      :prop="'children.' + scope.$index + '.name'"
                      :rules="{ required: true, message: $t('OrderFoodSpecEdit.PleaseEnterFoodSpecOptions'), trigger: ['change', 'blur'] }"
                    >
                      <el-input
                        v-model="scope.row.name"
                        maxlength="10"
                        :placeholder="$t('OrderFoodSpecEdit.SpecOptionPlaceholder')"
                      />
                    </el-form-item>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('OrderFoodItemManagement.Action')" width="100" align="center">
                  <template slot-scope="scope">
                    <el-button
                      plain
                      type="danger"
                      size="mini"
                      @click="handleDeleteOption(scope.$index)"
                    >
                      {{ $t("General.Delete") }}
                    </el-button>
                  </template>
                </el-table-column>
                <template slot="empty">
                  <span></span>
                </template>
              </el-table>
            </ItemsListSortable>
            <div class="food-item-edit__add-btn-container">
              <el-button plain icon="el-icon-plus" type="primary" @click="addSpecOption">
                {{ $t("OrderFoodSpecEdit.CreateSpecOption") }}
              </el-button>
            </div>
          </div>
        </el-form>
      </div>
      <el-divider></el-divider>
      <div class="footer-container">
        <el-button
          round
          @click="handleClickCancel"
        >
          {{ $t("General.Cancel") }}
        </el-button>
        <el-button
          type="primary"
          round
          @click="handleSave"
        >
          {{ $t("General.Store") }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import ItemsListSortable from './components/ItemsListSortable.vue'
import { apiOrderFood } from '@/api/OrderFood'
import { convertKeysToPascalCase, convertKeysToCamelCase } from '@/js/utils/bff'
export default {
  name: 'OrderFoodSpecEdit',
  components: {
    ItemsListSortable
  },
  data () {
    return {
      id: '0',
      mode: 'add', // 模式(預設為新增)
      messageTitle: '規格新增',
      editFormRef: null, // 表單實例
      // 表單資料
      editForm: {
        name: '', // 規格名稱
        originalName: '', // 顯示刪除名稱
        sequence: 999, // 排序
        children: [] // 規格選項
      },
      originalEditFormData: '', // 原始表單資料
      deleteChildren: [], // 刪除的規格選項
      childrenIdTemplate: '00000000-0000-0000-0000-000000000000', // 規格選項id模板
      sortableRef: null, // 排序實例
      tableRef: null, // 表格實例
      loading: null, // 加載實例
      usedItems: [] // 已使用的項目
    }
  },
  async mounted () {
    this.$loading({
      lock: true,
      text: this.$t('General.PleaseWaitFor', { action: this.$t('OrderFood.InitializingData') }),
      spinner: 'el-icon-loading',
      background: 'rgba(0, 0, 0, 0.7)'
    })
    try {
      await this.$store.dispatch('getFoodSpec')
    } catch (error) {
      console.log(error)
      this.$message({
        message: this.$t('OrderFoodSpecEdit.GetSpecDataFailed'),
        type: 'error'
      })
    } finally {
      this.$loading().close()
    }

    this.id = this.$route.params.id
    // 如果id不為0，則為編輯模式
    if (this.id !== '0') {
      this.mode = 'edit'
      this.messageTitle = this.$t('OrderFoodSpecEdit.SpecEdit')
      this.handleGetSpecItem(this.id)
      this.usedItems = await this.handleGetUsedItems(this.id)
    }
    // 保留原始表單資料
    this.originalEditFormData = JSON.stringify(this.editForm)
  },
  methods: {
    /**
     * 處理取得規格資料
     * 1. 從 Vuex 取得規格資料
     */
    handleGetSpecItem (id) {
      const specItem = this.$store.getters.getSpecItem(id)
      if (specItem) {
        Object.assign(this.editForm, _cloneDeep(specItem))
        this.editForm.originalName = this.editForm.name
      }
      this.editForm.children.forEach((item, index) => {
        item.timestamp = this.getRandomTimestamp() // 用於 table row-key
        item.originalName = item.name
      })
      this.$nextTick(() => {
        this.initSortable()
      })
    },
    /**
     * 處理刪除規格
     */
    handleClickDelete () {
      // msgbox 設定值
      const options = {
        showClose: false,
        showCancelButton: true,
        cancelButtonText: this.$t('General.Cancel'),
        confirmButtonText: this.$t('General.Confirm'),
        confirmButtonClass: 'el-button--primary',
        dangerouslyUseHTMLString: true,
        callback: action => {
          if (action === 'confirm') {
            this.handleDeleteSpec()
          }
        }
      }

      const title = this.$t('OrderFoodSpecEdit.IfDeleteTargetSpec', { targetName: this.editForm.name || this.editForm.originalName })
      let message = this.$t('OrderFoodSpecEdit.DeleteFoodSpecTip')
      if (this.usedItems.length > 0) {
        let itemsString = this.usedItems.filter((item, index) => index < 10).map(item => item).join('、')
        if (this.usedItems.length > 10) {
          itemsString += '...'
        }
        message = `
          ${this.$t('OrderFoodSpecEdit.IfDeleteSpecItemsUsed', { foodItemsCount: this.usedItems.length, targetSpec: this.editForm.name || this.editForm.originalName })}<br>
          <br>
          ${this.$t('OrderFoodSpecEdit.CurrentFoodItem')}：${itemsString}
        `
      } else {
        // 依照 miro 設計，如果沒有使用此規格的餐點，才會顯示上述設定的 title
        Object.assign(options, { title })
      }
      // 加入訊息內容
      Object.assign(options, { message })

      this.$msgbox(options)
    },
    /**
     * 處理刪除規格
     */
    async handleDeleteSpec () {
      let isSuccessful = false
      const loading = this.$loading({
        lock: true,
        text: this.$t('General.PleaseWaitFor', { action: this.$t('General.Processing') }),
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      try {
        const messageTitle = this.$t('OrderFoodSpecEdit.SpecDelete')
        const { status } = convertKeysToCamelCase((await apiOrderFood.deleteFoodSpec(this.id)).data)
        if (status === 1) {
          isSuccessful = true
          this.$message({
            message: messageTitle + this.$t('General.Success'),
            type: 'success'
          })
          this.$router.go(-1)
        }
      } catch (error) {
        console.log(error)
      } finally {
        if (!isSuccessful) {
          this.$message({
            message: messageTitle + this.$t('General.Fail'),
            type: 'error'
          })
        }
        loading.close()
      }
    },
    // 新增規格選項
    addSpecOption () {
      if (this.mode === 'add') {
        this.editForm.children.push({
          timestamp: this.getRandomTimestamp(), // 用於 table row-key
          name: '',
          sequence: 0
        })
      } else if (this.mode === 'edit') {
        this.editForm.children.push({
          timestamp: this.getRandomTimestamp(), // 用於 table row-key
          id: this.childrenIdTemplate, // 新增給空白id
          name: '',
          sequence: this.editForm.children.length + 1,
          isCheck: 1
        })
      }
      this.initSortable()
    },
    getRandomTimestamp () {
      return `${Date.now()}-${Math.floor(Math.random() * 10000)}`
    },
    /**
     * 處理刪除規格選項
     * @param {number} index 規格選項索引
     */
    async handleDeleteOption (index) {
      const targetChildren = this.editForm.children[index]
      if (this.mode === 'add' || (this.mode === 'edit' && targetChildren.id === this.childrenIdTemplate)) {
        this.editForm.children.splice(index, 1)
      } else if (this.mode === 'edit') {
        const usedItems = await this.handleGetUsedItems(targetChildren.id)
        // msgbox 設定值
        const options = {
          showClose: false,
          showCancelButton: true,
          cancelButtonText: this.$t('General.Cancel'),
          confirmButtonText: this.$t('General.Confirm'),
          confirmButtonClass: 'el-button--primary',
          dangerouslyUseHTMLString: true,
          callback: action => {
            if (action === 'confirm') {
              this.deleteChildren.push(this.editForm.children.splice(index, 1)[0])
            }
          }
        }

        let message = this.$t('OrderFoodSpecEdit.IfDeleteTargetSpecOption', { targetName: targetChildren.name || targetChildren.originalName })
        let itemsString = usedItems.filter((item, index) => index < 10).map(item => item).join('、')
        if (usedItems.length > 0) {
          if (usedItems.length > 10) {
            itemsString += '...'
          }
          message = `
              ${this.$t('OrderFoodSpecEdit.IfDeleteSpecOptionItemsUsed', { foodItemsCount: usedItems.length, targetSpec: targetChildren.name || targetChildren.originalName })}<br>
              <br>
              ${this.$t('OrderFoodSpecEdit.CurrentFoodItem')}：${itemsString}
            `
        }

        // 加入訊息內容
        Object.assign(options, { message })
        this.$msgbox(options)
      }
    },
    // 初始化排序實例
    initSortable () {
      this.$nextTick(() => {
        this.$refs.sortableRef.destroy()
        this.$refs.sortableRef.init()
      })
    },
    // 處理拖曳結束
    handleDragEnd ({ oldIndex, newIndex }) {
      const movedItem = this.editForm.children.splice(oldIndex, 1)[0]
      this.editForm.children.splice(newIndex, 0, movedItem)
      this.editForm.children.forEach((item, index) => {
        item.sequence = index + 1
      })
    },
    // 處理儲存
    async handleSave () {
      try {
        await this.$refs.editFormRef.validate()
      } catch (error) {
        console.log(error)
        return
      }

      let isSuccessful = false
      try {
        this.loading = this.$loading({
          lock: true,
          text: this.$t('General.PleaseWaitFor', { action: this.$t('General.Saving') }),
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        if (this.mode === 'add') {
          const params = {
            name: this.editForm.name,
            sequence: this.$store.getters.getSpecListLatestSequence + 1,
            children: this.editForm.children.map((item, index) => {
              return {
                name: item.name,
                sequence: index + 1
              }
            })
          }

          const { status } = convertKeysToCamelCase((await apiOrderFood.addFoodSpec(convertKeysToPascalCase(params))).data)
          if (status === 1) {
            isSuccessful = true
            this.$message({
              message: this.messageTitle + this.$t('General.Success'),
              type: 'success'
            })
            this.$router.go(-1)
          }
        } else if (this.mode === 'edit') {
          const params = {
            name: this.editForm.name,
            isCheck: 1,
            children: this.editForm.children.map((item, index) => {
              return {
                id: item.id,
                name: item.name,
                sequence: index + 1,
                isCheck: 1
              }
            })
          }
          // 如果有刪除的規格選項
          if (this.deleteChildren.length > 0) {
            this.deleteChildren.forEach((item) => {
              params.children.push({
                id: item.id,
                name: item.name,
                sequence: item.sequence,
                isCheck: -1 // 刪除
              })
            })
          }

          const { status } = convertKeysToCamelCase((await apiOrderFood.updateFoodSpec(this.id, convertKeysToPascalCase(params))).data)
          if (status === 1) {
            isSuccessful = true
            this.$message({
              message: this.messageTitle + this.$t('General.Success'),
              type: 'success'
            })
            this.$router.go(-1)
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        if (!isSuccessful) {
          this.$message({
            message: this.messageTitle + this.$t('General.Fail'),
            type: 'error'
          })
        }
        this.loading && this.loading.close()
      }
    },
    /**
     * 處理取得已使用的項目
     * @param {string} targetSpecId 目標規格id
     */
    async handleGetUsedItems (targetSpecId) {
      let dataList = []
      const loading = this.$loading({
        lock: true,
        text: this.$t('General.PleaseWaitFor', { action: this.$t('General.Loading') }),
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      try {
        const { status, data } = convertKeysToCamelCase((await apiOrderFood.getFoodSpecUsedItems(targetSpecId)).data)
        if (status === 1) {
          if (data.items.length > 0) {
            dataList = [...data.items]
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        loading.close()
        return dataList
      }
    },
    /**
     * 處理點擊取消
     */
    handleClickCancel () {
      if (JSON.stringify(this.editForm) !== this.originalEditFormData) {
        this.$msgbox({
          title: this.$t('OrderFoodItemManagement.NotSavedYet'),
          message: this.$t('OrderFoodItemManagement.IfLeaveChangeNotSaved'),
          showCancelButton: true,
          confirmButtonText: this.$t('General.Leave'),
          cancelButtonText: this.$t('OrderFoodItemManagement.KeepEdit'),
          confirmButtonClass: 'el-button--danger',
          callback: action => {
            if (action === 'confirm') {
              this.$router.go(-1)
            }
          }
        })
      } else {
        this.$router.go(-1)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.food-item-edit {
  @include FoodItemEdit();
  &__form-el {
    width: 650px;
  }
}
</style>
